<ng-container *ngIf="isOpen">

  <!-- ── Backdrop ─────────────────────────────────────────── -->
  <div class="nm-backdrop"
       @backdropAnim
       (click)="onOverlayClick()"
       aria-hidden="true">
  </div>

  <!-- ── Panel ────────────────────────────────────────────── -->
  <div class="nm-panel"
       @panelAnim
       role="dialog"
       aria-modal="true"
       [attr.aria-label]="mode === 'modules' ? 'All Modules' : (selectedModule?.label ?? 'Pages')"
       (click)="$event.stopPropagation()">

    <!-- ═══ MODULES VIEW ═══ -->
    <ng-container *ngIf="mode === 'modules'">
      <div class="nm-view" @viewSlideLeft>

        <!-- Header -->
        <div class="nm-header">
          <div class="nm-header-left">
            <div class="nm-header-icon-badge">
              <mat-icon>apps</mat-icon>
            </div>
            <div class="nm-header-text">
              <h2 class="nm-title">All Modules</h2>
              <span class="nm-subtitle">{{ moduleCards.length }} modules available</span>
            </div>
          </div>
          <button class="nm-close-btn" (click)="close()" type="button" aria-label="Close navigation">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- Divider -->
        <div class="nm-divider"></div>

        <!-- Modules Grid -->
        <div class="nm-body" [@staggerCards]="moduleCards.length">
          <div class="nm-modules-grid">
            <div
              *ngFor="let card of moduleCards; trackBy: trackByModule"
              class="nm-module-card"
              (click)="onModuleSelect(card)"
              (keydown.enter)="onModuleSelect(card)"
              (keydown.space)="$event.preventDefault(); onModuleSelect(card)"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Open ' + card.item.label">

              <!-- Top gradient bar -->
              <span class="mc-top-bar"></span>

              <!-- Icon badge -->
              <div class="mc-icon-ring">
                <div class="mc-icon-wrap">
                  <mat-icon>{{ card.icon }}</mat-icon>
                </div>
              </div>

              <!-- Text -->
              <div class="mc-body">
                <div class="mc-name">{{ card.item.label }}</div>
                <div class="mc-desc">{{ card.description }}</div>
              </div>

              <!-- Browse action -->
              <div class="mc-footer">
                <span class="mc-open-label">Browse Pages</span>
                <span class="mc-arrow-chip">
                  <mat-icon>arrow_forward</mat-icon>
                </span>
              </div>

            </div>
          </div>
        </div>

      </div>
    </ng-container>

    <!-- ═══ PAGES VIEW ═══ -->
    <ng-container *ngIf="mode === 'pages'">
      <div class="nm-view" @viewSlideRight>

        <!-- Header -->
        <div class="nm-header">
          <div class="nm-header-left">
            <button class="nm-back-btn" (click)="goBackToModules()" type="button" aria-label="Back to all modules">
              <mat-icon>arrow_back</mat-icon>
              <span>All Modules</span>
            </button>
            <span class="nm-header-sep"></span>
            <div class="nm-header-icon-badge pages-badge">
              <mat-icon>{{ getModuleIcon(selectedModule?.label ?? '') }}</mat-icon>
            </div>
            <div class="nm-header-text">
              <h2 class="nm-title">{{ selectedModule?.label }}</h2>
              <span class="nm-subtitle">{{ selectedModulePages.length }} pages</span>
            </div>
          </div>
          <button class="nm-close-btn" (click)="close()" type="button" aria-label="Close navigation">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- Divider -->
        <div class="nm-divider"></div>

        <!-- Pages Grid -->
        <div class="nm-body" [@staggerCards]="selectedModulePages.length">

          <!-- Empty state -->
          <div *ngIf="selectedModulePages.length === 0" class="nm-empty-state">
            <mat-icon>folder_open</mat-icon>
            <p>No pages found for this module.</p>
          </div>

          <div class="nm-pages-grid" *ngIf="selectedModulePages.length > 0">
            <ng-container *ngFor="let page of selectedModulePages; trackBy: trackByPage">

              <!-- Simple page (no children) -->
              <div
                *ngIf="!page.hasChildren"
                class="nm-page-card"
                (click)="onPageClick(page)"
                (keydown.enter)="onPageClick(page)"
                (keydown.space)="$event.preventDefault(); onPageClick(page)"
                tabindex="0"
                role="button"
                [attr.aria-label]="'Navigate to ' + page.label">

                <span class="pc-top-bar"></span>

                <div class="pc-icon-ring">
                  <div class="pc-icon-wrap">
                    <mat-icon>{{ page.icon || 'chevron_right' }}</mat-icon>
                  </div>
                </div>

                <div class="pc-body">
                  <div class="pc-name">{{ page.label }}</div>
                </div>

                <div class="pc-footer">
                  <span class="pc-open-label">Open Page</span>
                  <span class="pc-arrow-chip">
                    <mat-icon>arrow_forward</mat-icon>
                  </span>
                </div>

              </div>

              <!-- Group page (has children) -->
              <div
                *ngIf="page.hasChildren"
                class="nm-page-group"
                [class.expanded]="page.expanded">

                <!-- Trigger button ────────────────────── -->
                <button
                  class="pg-trigger"
                  (click)="togglePageGroup(page, $event)"
                  type="button"
                  [attr.aria-expanded]="page.expanded"
                  [attr.aria-label]="page.label + ' – ' + (page.children?.length || 0) + ' sub-pages'">

                  <span class="pc-top-bar"></span>

                  <div class="pc-icon-ring">
                    <div class="pg-trigger-icon" [class.active]="page.expanded">
                      <mat-icon>{{ page.icon || 'folder' }}</mat-icon>
                    </div>
                  </div>

                  <div class="pg-trigger-text">
                    <span class="pg-trigger-name">{{ page.label }}</span>
                    <span class="pg-trigger-meta">
                      <mat-icon>layers</mat-icon>
                      {{ page.children?.length }} sub-pages
                    </span>
                  </div>

                  <div class="pc-footer">
                    <span class="pc-open-label">{{ page.expanded ? 'Collapse' : 'Expand' }}</span>
                    <div class="pg-chevron" [class.rotated]="page.expanded">
                      <mat-icon>expand_more</mat-icon>
                    </div>
                  </div>

                </button>

                <!-- Dropdown body ────────────────────── -->
                <div class="pg-dropdown" *ngIf="page.expanded" @dropdownExpand>

                  <!-- Section header -->
                  <div class="pg-dropdown-header">
                    <span class="pg-dropdown-header-line"></span>
                    <span class="pg-dropdown-header-label">
                      <mat-icon>grid_view</mat-icon>
                      {{ page.children?.length }} Sub-pages
                    </span>
                    <span class="pg-dropdown-header-line"></span>
                  </div>

                  <!-- Sub-items grid -->
                  <div class="pg-sub-grid" [@childStagger]="page.children?.length">
                    <button
                      *ngFor="let child of page.children; let i = index; trackBy: trackByPage"
                      class="pg-sub-card"
                      (click)="onSubPageClick(child)"
                      type="button"
                      [attr.aria-label]="'Navigate to ' + child.label">

                      <!-- Top accent bar -->
                      <span class="pg-sub-top-bar"></span>

                      <!-- Number badge -->
                      <span class="pg-sub-num">{{ i + 1 }}</span>

                      <!-- Icon -->
                      <div class="pg-sub-icon">
                        <mat-icon>{{ child.icon || 'chevron_right' }}</mat-icon>
                      </div>

                      <!-- Label -->
                      <span class="pg-sub-label">{{ child.label }}</span>

                      <!-- Go arrow -->
                      <span class="pg-sub-go">
                        <mat-icon>arrow_forward</mat-icon>
                      </span>

                    </button>
                  </div>

                </div>

              </div>

            </ng-container>
          </div>

        </div>

      </div>
    </ng-container>

  </div>

</ng-container>
